import { jsPDF } from 'jspdf';

// Colors pulled from the app's design tokens (src/components/vps/theme.js),
// expressed as RGB triplets for jsPDF's setFillColor/setTextColor/etc.
const COLORS = {
  primary: [37, 99, 235],
  success: [16, 185, 129],
  successLight: [236, 253, 245],
  warning: [217, 119, 6],
  warningLight: [255, 251, 235],
  fail: [220, 38, 38],
  failLight: [254, 242, 242],
  textPrimary: [15, 23, 42],
  textSecondary: [71, 85, 105],
  textMuted: [100, 116, 139],
  border: [226, 232, 240],
  bgSection: [248, 250, 252],
  white: [255, 255, 255],
};

// "YYYY-MM-DD HH:mm:ss" -> { date: "06 Aug 2026", time: "01:09 PM" }
const formatDateTime = (str) => {
  if (!str) return { date: '-', time: '' };
  const [datePart, timePart] = str.split(' ');
  const [y, m, d] = (datePart || '').split('-').map(Number);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const date = (y && m && d) ? `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}` : '-';
  let time = '';
  if (timePart) {
    let [hh, mm] = timePart.split(':').map(Number);
    const ampm = hh >= 12 ? 'PM' : 'AM';
    hh = hh % 12 || 12;
    time = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${ampm}`;
  }
  return { date, time };
};

const formatAmount = (amount, currency = 'INR') => {
  const num = parseFloat(amount) || 0;
  const symbol = currency === 'INR' ? 'Rs. ' : `${currency} `;
  return `${symbol}${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getStatusMeta = (payment) => {
  const status = (payment.payment_status || '').toLowerCase();
  if (status === 'success') {
    return { color: COLORS.success, bg: COLORS.successLight, label: 'Paid Successfully', type: 'success' };
  }
  if (status === 'pending') {
    return { color: COLORS.warning, bg: COLORS.warningLight, label: 'Payment Pending', type: 'pending' };
  }
  return { color: COLORS.fail, bg: COLORS.failLight, label: 'Payment Failed', type: 'failed' };
};

// Fetches the brand logo from the public folder (CRA copies everything in
// `public/` to the app's root at build time, so this is always a same-origin,
// root-relative request — no env var/hardcoded domain needed) and converts
// it to a base64 data URL so jsPDF can embed it. Returns null if it can't be
// loaded, so the invoice can still fall back to a plain text header instead
// of failing.
const LOGO_PATH = `${process.env.PUBLIC_URL}/assets/vps-logo/logo-with-bg.png`;

const loadLogoDataUrl = async () => {
  try {
    const res = await fetch(LOGO_PATH);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const drawStatusIcon = (doc, cx, cy, r, color, type) => {
  doc.setFillColor(...color);
  doc.circle(cx, cy, r, 'F');
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(1.8);
  if (type === 'success') {
    doc.line(cx - r * 0.45, cy, cx - r * 0.08, cy + r * 0.4);
    doc.line(cx - r * 0.08, cy + r * 0.4, cx + r * 0.5, cy - r * 0.35);
  } else if (type === 'pending') {
    doc.line(cx, cy, cx, cy - r * 0.5);
    doc.line(cx, cy, cx + r * 0.4, cy + r * 0.05);
  } else {
    doc.line(cx - r * 0.4, cy - r * 0.4, cx + r * 0.4, cy + r * 0.4);
    doc.line(cx - r * 0.4, cy + r * 0.4, cx + r * 0.4, cy - r * 0.4);
  }
};

/**
 * Builds and downloads a PDF invoice for a single payment record, styled to
 * match the app's on-brand invoice design (no third-party gateway branding).
 * @param {object} payment - one row from the /getPayments response.
 */
export const generateInvoicePdf = async (payment) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  const contentWidth = pageWidth - margin * 2;

  const { date, time } = formatDateTime(payment.created_date);
  const meta = getStatusMeta(payment);
  const amountLabel = formatAmount(payment.amount, payment.currency);
  const logoDataUrl = await loadLogoDataUrl();

  let y = margin;

  // ---- Brand header ----
  // headerBottom tracks the lowest point actually drawn in the header (logo
  // or fallback badge), so the divider below it never overlaps the logo —
  // that was the bug: a fixed offset here clipped tall logos.
  let textStartX = margin + 38;
  let headerBottom = y + 26;
  if (logoDataUrl) {
    // Logo file (logo-invoice.png) is a cropped, white-flattened version of
    // the brand logo at 1011x431 (~2.35:1) — keep that aspect ratio instead
    // of stretching it into a square.
    const logoHeight = 40;
    const logoWidth = logoHeight * (1011 / 431);
    try {
      doc.addImage(logoDataUrl, 'PNG', margin, y, logoWidth, logoHeight);
      textStartX = margin + logoWidth + 12;
      headerBottom = y + logoHeight;
    } catch {
      // Fall through to the text-only fallback below.
    }
  }
  if (!logoDataUrl) {
    doc.setFillColor(...COLORS.primary);
    doc.roundedRect(margin, y, 26, 26, 5, 5, 'F');
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('H', margin + 13, y + 18, { align: 'center' });
    doc.setTextColor(...COLORS.textPrimary);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Hosting Services', textStartX, y + 18);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.textMuted);
  doc.text('INVOICE', pageWidth - margin, y + 18, { align: 'right' });

  y = headerBottom + 18;
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(1);
  doc.line(margin, y, pageWidth - margin, y);

  // ---- Status banner ----
  const bannerHeight = 64;
  doc.setFillColor(...meta.bg);
  doc.rect(margin, y, contentWidth, bannerHeight, 'F');
  drawStatusIcon(doc, margin + 26, y + bannerHeight / 2, 13, meta.color, meta.type);

  doc.setTextColor(...meta.color);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(amountLabel, margin + 54, y + bannerHeight / 2 - 2);

  doc.setTextColor(...COLORS.textSecondary);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`${meta.label}${date !== '-' ? ` on ${date}` : ''}`, margin + 54, y + bannerHeight / 2 + 16);

  y += bannerHeight;
  doc.setDrawColor(...COLORS.border);
  doc.line(margin, y, pageWidth - margin, y);
  y += 26;

  // ---- Payment Details ----
  doc.setTextColor(...COLORS.textPrimary);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Payment Details', margin, y);
  y += 22;

  const col2X = margin + contentWidth / 2;
  const labelStyle = () => { doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...COLORS.textMuted); };
  const valueStyle = (color = COLORS.textPrimary) => { doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(...color); };

  labelStyle(); doc.text('TRANSACTION ID', margin, y);
  labelStyle(); doc.text('ORDER ID', col2X, y);
  y += 14;
  valueStyle(); doc.text(String(payment.cashfree_order_id || '-'), margin, y);
  valueStyle(); doc.text(String(payment.order_id || '-'), col2X, y);
  y += 26;

  labelStyle(); doc.text('TRANSACTION DATE', margin, y);
  labelStyle(); doc.text('STATUS', col2X, y);
  y += 14;
  valueStyle(); doc.text(`${date}${time ? `, ${time}` : ''}`, margin, y);
  valueStyle(meta.color); doc.text(String(payment.payment_status || '-'), col2X, y);
  y += 30;

  doc.setDrawColor(...COLORS.border);
  doc.line(margin, y, pageWidth - margin, y);
  y += 26;

  // ---- Customer Details ----
  doc.setTextColor(...COLORS.textPrimary);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Customer Details', margin, y);
  y += 20;

  labelStyle(); doc.text('ISSUED TO', margin, y);
  y += 15;
  valueStyle(); doc.text(String(payment.customer_name || '-'), margin, y);
  y += 15;

  if (payment.customer_email) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.primary);
    doc.textWithLink(payment.customer_email, margin, y, { url: `mailto:${payment.customer_email}` });
    y += 15;
  }
  if (payment.customer_phone) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textSecondary);
    doc.text(`+91 ${payment.customer_phone}`, margin, y);
    y += 15;
  }

  y += 14;
  doc.setDrawColor(...COLORS.border);
  doc.line(margin, y, pageWidth - margin, y);
  y += 26;

  // ---- Line items table ----
  const tableTop = y;
  const rowHeight = 26;
  const headerHeight = 24;

  doc.setDrawColor(...COLORS.border);
  doc.setFillColor(...COLORS.bgSection);
  doc.rect(margin, tableTop, contentWidth, headerHeight, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.textMuted);
  doc.text('DESCRIPTION', margin + 12, tableTop + 15);
  doc.text('AMOUNT', pageWidth - margin - 12, tableTop + 15, { align: 'right' });

  let rowY = tableTop + headerHeight;
  doc.line(margin, rowY, pageWidth - margin, rowY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.textPrimary);
  doc.text(String(payment.plan_name || 'VPS Hosting Plan'), margin + 12, rowY + 17);
  doc.setTextColor(...COLORS.textSecondary);
  doc.text(amountLabel, pageWidth - margin - 12, rowY + 17, { align: 'right' });

  rowY += rowHeight;
  doc.setDrawColor(...COLORS.border);
  doc.line(margin, rowY, pageWidth - margin, rowY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...COLORS.textPrimary);
  doc.text('Total Amount', margin + 12, rowY + 17);
  doc.setTextColor(...COLORS.primary);
  doc.text(amountLabel, pageWidth - margin - 12, rowY + 17, { align: 'right' });

  rowY += rowHeight;
  doc.setDrawColor(...COLORS.border);
  doc.rect(margin, tableTop, contentWidth, rowY - tableTop);

  y = rowY + 40;
  doc.setDrawColor(...COLORS.border);
  doc.line(margin, y, pageWidth - margin, y);
  y += 26;

  // ---- Footer ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.textPrimary);
  doc.text('Hosting Services', pageWidth / 2, y, { align: 'center' });
  y += 16;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.textMuted);
  doc.text('In case of complaint or feedback, please contact Hosting Services', pageWidth / 2, y, { align: 'center' });
  y += 18;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.primary);
  const emailText = 'Email';
  const sepText = '   |   ';
  const websiteText = 'Visit Our Website';
  const emailWidth = doc.getTextWidth(emailText);
  const sepWidth = doc.getTextWidth(sepText);
  const websiteWidth = doc.getTextWidth(websiteText);
  const totalWidth = emailWidth + sepWidth + websiteWidth;
  let fx = pageWidth / 2 - totalWidth / 2;

  doc.textWithLink(emailText, fx, y, { url: 'mailto:support@hostingservices.co.in' });
  fx += emailWidth;
  doc.setTextColor(...COLORS.border);
  doc.text(sepText, fx, y);
  fx += sepWidth;
  doc.setTextColor(...COLORS.primary);
  doc.textWithLink(websiteText, fx, y, { url: 'https://hostingservices.co.in' });

  const fileName = `Invoice-${payment.order_id || payment.id || 'hostingservices'}.pdf`;
  doc.save(fileName);
};

export default generateInvoicePdf;